import * as ibantools from 'ibantools';
import { DateTime } from 'luxon';

import {
  BasicCompanyInfo,
  Beneficiaries,
  KybSubmissionDocument,
  DocumentType,
  KybSubmissionData,
  BankAccounts,
  BankAccountType,
  BusinessRepresentative as BusinessRepresentativeApi,
} from 'apis/legitimizer/types';

import { COUNTRY_CODE_EXTENSIONS } from './countryCodes';
import {
  BankDetailsFormValues,
  BasicInfoFormValues,
  BeneficiariesFormValues,
  BusinessRepresentative,
  DocumentsFormValues,
  PhoneNumber,
} from './types';
import { createEmptyDocumentUpload } from './utils';

const valueOrNull = <T>(value: T | undefined) => (value !== undefined ? { value } : null);

const notBlankValueOrNull = (value?: string | undefined) =>
  value === undefined || value?.toString()?.trim() === '' ? null : { value };

const convertDateAndYear = (date: Date, year: string) => {
  date.setFullYear(+year);
  return DateTime.fromJSDate(date).toISODate();
};

const convertToDate = (dateStr: string): Date => DateTime.fromISO(dateStr).toJSDate();

export const transformBasicInfoFormValuesToApi = (formValues: BasicInfoFormValues): BasicCompanyInfo => ({
  businessDetails: {
    entityUUID: formValues?.entityUUID ?? null,
    companyName: valueOrNull(formValues.companyName?.trim()),
    streetAddress: valueOrNull(formValues.streetAddress?.trim()),
    city: valueOrNull(formValues.city?.trim()),
    postalCode: valueOrNull(formValues.postalCode?.trim()),
    countryCode: valueOrNull(formValues.countryCode?.trim()),
    registrationNumber: valueOrNull(formValues.registrationNumber?.trim()),
    isVatLiable: valueOrNull(formValues.isVatLiable),
    vatId: valueOrNull(formValues.vatId?.trim()),
    tin: notBlankValueOrNull(formValues.tin?.trim()),
    lucidId: notBlankValueOrNull(formValues.lucidId?.trim()),
    legalForm: valueOrNull(formValues.legalForm?.trim()),
    dateOfRegistration:
      formValues.dateOfRegistration && formValues.yearOfRegistration
        ? valueOrNull(convertDateAndYear(formValues.dateOfRegistration, formValues.yearOfRegistration))
        : null,
    taxCountryCode: valueOrNull(formValues.taxCountryCode?.trim()),
    fieldOfIndustry: valueOrNull(formValues.fieldOfIndustry?.trim()),
    phoneNumber: formValues.phone?.number?.trim()
      ? valueOrNull(`${formValues.phone.extension?.trim() ?? ''}${formValues.phone.number?.trim()}`)
      : null,
  },
  businessRepresentatives:
    formValues.businessRepresentatives?.map(r => ({
      entityUUID: r.entityUUID ?? null,
      fullName: valueOrNull(r.name?.trim()),
      nationality: valueOrNull(r.nationality),
      nationalId: valueOrNull(r.nationalId?.trim()),
      countryOfResidence: valueOrNull(r.countryOfResidence),
      dateOfBirth:
        r.dateOfBirth && r.yearOfBirth ? valueOrNull(convertDateAndYear(r.dateOfBirth, r.yearOfBirth)) : null,
      placeOfBirth: valueOrNull(r.placeOfBirth),
      isMainRepresentative: valueOrNull(r.isMainRepresentative),
      userId: valueOrNull(r.userId),
    })) ?? null,
});

export const transformBeneficiariesFormValuesToApi = (formValues: BeneficiariesFormValues): Beneficiaries => ({
  uboDetails:
    formValues.beneficiaries?.map(b => ({
      entityUUID: b.entityUUID ?? null,
      fullName: valueOrNull(b.name?.trim()),
      streetAddress: valueOrNull(b.street?.trim()),
      city: valueOrNull(b.city?.trim()),
      postalCode: valueOrNull(b.postalCode?.trim()),
      nationality: valueOrNull(b.nationality),
      nationalId: valueOrNull(b.nationalId?.trim()),
      country: valueOrNull(b.country),
      dateOfBirth:
        b.dateOfBirth && b.yearOfBirth ? valueOrNull(convertDateAndYear(b.dateOfBirth, b.yearOfBirth)) : null,
    })) ?? [],
  pepDetails:
    formValues.peps?.map(p => ({
      entityUUID: p.entityUUID ?? null,
      fullName: valueOrNull(p.name?.trim()),
      officialPosition: valueOrNull(p.officialPosition?.trim()),
      countryCode: valueOrNull(p.country),
      expirationDate:
        p.dateOfExpiration && p.yearOfExpiration
          ? valueOrNull(convertDateAndYear(p.dateOfExpiration, p.yearOfExpiration))
          : null,
    })) ?? [],
});

const separateIntoPhoneNumberComponents = (phone: string | undefined): PhoneNumber => {
  const splitNumber = (matchingCode: string) => {
    const [, number] = phone!.split(matchingCode);
    return number === '' ? undefined : number;
  };

  if (phone === undefined) {
    return { extension: undefined, number: undefined };
  }
  const matchingCode = COUNTRY_CODE_EXTENSIONS.map(c => c.id).find(v => phone.includes(v));
  return matchingCode === undefined
    ? {
        extension: undefined,
        number: phone,
      }
    : {
        extension: matchingCode,
        number: splitNumber(matchingCode),
      };
};

const transformBusinessRepresentativesToFormValues = (
  businessRepresentatives: BusinessRepresentativeApi[] | null | undefined,
): BusinessRepresentative[] => {
  const entries: BusinessRepresentative[] | undefined = businessRepresentatives?.map(r => ({
    entityUUID: r.entityUUID ?? undefined,
    name: r.fullName?.value ?? undefined,
    dateOfBirth: r.dateOfBirth?.value ? convertToDate(r.dateOfBirth?.value) : undefined,
    yearOfBirth: r.dateOfBirth?.value ? convertToDate(r.dateOfBirth?.value).getFullYear().toString() : undefined,
    placeOfBirth: r.placeOfBirth?.value ?? undefined,
    nationality: r.nationality?.value ?? undefined,
    nationalId: r.nationalId?.value ?? undefined,
    countryOfResidence: r.countryOfResidence?.value ?? undefined,
    isMainRepresentative: !!r.isMainRepresentative?.value,
    userId: r.userId?.value ?? undefined,
  }));
  return entries && entries.length > 0 ? entries : [{ name: undefined, isMainRepresentative: false }];
};

export const transformKybSubmissionToBasicInfoFormValues = (
  data: BasicCompanyInfo | null,
  countryCode: string | undefined = undefined,
): BasicInfoFormValues => {
  return {
    entityUUID: data?.businessDetails?.entityUUID ?? undefined,
    companyName: data?.businessDetails?.companyName?.value ?? undefined,
    streetAddress: data?.businessDetails?.streetAddress?.value ?? undefined,
    postalCode: data?.businessDetails?.postalCode?.value ?? undefined,
    city: data?.businessDetails?.city?.value ?? undefined,
    countryCode: data?.businessDetails?.countryCode?.value ?? countryCode,
    registrationNumber: data?.businessDetails?.registrationNumber?.value ?? undefined,
    isVatLiable: data?.businessDetails?.isVatLiable?.value ?? undefined,
    vatId: data?.businessDetails?.vatId?.value ?? undefined,
    tin: data?.businessDetails?.tin?.value ?? undefined,
    lucidId: data?.businessDetails?.lucidId?.value ?? undefined,
    legalForm: data?.businessDetails?.legalForm?.value ?? undefined,
    dateOfRegistration: data?.businessDetails?.dateOfRegistration?.value
      ? convertToDate(data?.businessDetails?.dateOfRegistration?.value)
      : undefined,
    yearOfRegistration: data?.businessDetails?.dateOfRegistration?.value
      ? convertToDate(data?.businessDetails?.dateOfRegistration?.value).getFullYear().toString()
      : undefined,
    taxCountryCode: data?.businessDetails?.taxCountryCode?.value ?? countryCode,
    fieldOfIndustry: data?.businessDetails?.fieldOfIndustry?.value ?? undefined,
    phone: separateIntoPhoneNumberComponents(data?.businessDetails?.phoneNumber?.value ?? undefined),
    businessRepresentatives: transformBusinessRepresentativesToFormValues(data?.businessRepresentatives),
    nextSection: undefined,
  };
};

export const documentOfTypeExists = (documents: KybSubmissionDocument[], documentType: DocumentType): boolean => {
  return documents.some(document => document.documentType === documentType);
};

export const transformKybSubmissionDocumentsToDocumentsFormValues = (
  documents: KybSubmissionDocument[],
): DocumentsFormValues => {
  const registryExtractExists = documentOfTypeExists(documents, 'REGISTRATION_CERTIFICATE');

  return {
    registryExtractDocuments: !registryExtractExists ? [createEmptyDocumentUpload()] : [],
    nextSection: undefined,
  };
};

export const transformKybSubmissionToBankDetailsFormValues = (
  data: KybSubmissionData | null,
  documents: KybSubmissionDocument[],
): BankDetailsFormValues => {
  const details = data?.bankAccounts?.bankAccountDetails?.[0];
  const bankDocumentExists = documentOfTypeExists(documents, 'BANK_STATEMENT');

  return {
    entityUUID: details?.entityUUID ?? undefined,
    accountOwner: details?.accountOwner?.value ?? undefined,
    accountType: details?.accountType?.value ?? undefined,
    accountNumber: details?.accountNumber?.value ?? undefined,
    bankIdentifier: details?.bankId?.value ?? undefined,
    payoutRecipients: details?.payoutRecipients?.value?.join(', ') ?? undefined,
    bankDocuments: !bankDocumentExists ? [createEmptyDocumentUpload()] : [],
    nextSection: undefined,
  };
};

type BankAccountNumberSanitizer = (accountNumber?: string) => string | undefined;

const sanitizeIbanAccountNumber: BankAccountNumberSanitizer = accountNumber =>
  accountNumber ? (ibantools.electronicFormatIBAN(accountNumber) as string).replace(/[^a-zA-Z0-9 ]/g, '') : undefined;

const sanitizeBbanAccountNumber: BankAccountNumberSanitizer = accountNumber => accountNumber?.replace('-', '');

type SanitizeBankAccountNumber = (accountType?: BankAccountType) => BankAccountNumberSanitizer;

export const sanitizeAccountNumber: SanitizeBankAccountNumber = accountType => accountNumber => {
  if (!accountType) {
    return undefined;
  }

  const sanitizerMap: Record<BankAccountType, BankAccountNumberSanitizer> = {
    BBAN: sanitizeBbanAccountNumber,
    IBAN: sanitizeIbanAccountNumber,
  };
  const sanitizer = sanitizerMap[accountType];

  return sanitizer(accountNumber) || undefined;
};

export const transformBankDetailsFormValuesToApi = (formValues: BankDetailsFormValues): BankAccounts => {
  return {
    bankAccountDetails: [
      {
        entityUUID: formValues?.entityUUID ?? null,
        accountType: valueOrNull(formValues.accountType),
        accountOwner: valueOrNull(formValues.accountOwner?.trim()),
        accountNumber: valueOrNull(sanitizeAccountNumber(formValues.accountType)(formValues.accountNumber)?.trim()),
        bankId: formValues.accountType === 'IBAN' ? valueOrNull(formValues.bankIdentifier?.toUpperCase().trim()) : null,
        payoutRecipients: valueOrNull(
          formValues.payoutRecipients
            ?.split(',')
            .map(value => value.trim())
            .filter(value => !!value),
        ),
      },
    ],
  };
};

export const transformKybSubmissionToBeneficiariesFormValues = (
  data: Beneficiaries | null,
): BeneficiariesFormValues => {
  const uboDetails =
    data?.uboDetails?.map(ubo => ({
      entityUUID: ubo.entityUUID ?? undefined,
      name: ubo.fullName?.value ?? undefined,
      street: ubo.streetAddress?.value ?? undefined,
      postalCode: ubo.postalCode?.value ?? undefined,
      city: ubo.city?.value ?? undefined,
      country: ubo.country?.value ?? undefined,
      nationalId: ubo.nationalId?.value ?? undefined,
      nationality: ubo.nationality?.value ?? undefined,
      dateOfBirth: ubo.dateOfBirth?.value ? convertToDate(ubo.dateOfBirth?.value) : undefined,
      yearOfBirth: ubo.dateOfBirth?.value ? convertToDate(ubo.dateOfBirth?.value).getFullYear().toString() : undefined,
    })) ?? [];
  const peps =
    data?.pepDetails?.map(pep => ({
      entityUUID: pep.entityUUID ?? undefined,
      name: pep.fullName?.value ?? undefined,
      officialPosition: pep.officialPosition?.value ?? undefined,
      country: pep.countryCode?.value ?? undefined,
      dateOfExpiration: pep.expirationDate?.value ? convertToDate(pep.expirationDate?.value) : undefined,
      yearOfExpiration: pep.expirationDate?.value
        ? convertToDate(pep.expirationDate?.value).getFullYear().toString()
        : undefined,
    })) ?? [];

  return {
    beneficiaries: uboDetails.length > 0 ? uboDetails : [{ name: undefined }],
    peps: peps.length > 0 ? peps : [{ name: undefined }],
    nextSection: undefined,
  };
};

export const formatDate = (date: Date): string =>
  DateTime.fromJSDate(date).toLocaleString({ month: 'short', day: 'numeric' });
